import { useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import { getCountryCallingCode, getCountries, type Country } from "react-phone-number-input";
import { signUpDataAtom, pushStepAtom } from "store/signUpStore";
import { IdentityGlobalPhoneForm, IdentityGlobalPhoneResult } from "components/identity/IdentityGlobalPhoneForm";

/**
 * Step4: 국가코드 + 전화번호 입력 (해외 사용자 / KR 14세미만 본인 인증)
 *
 * 다음 → step5 (OTP 인증)
 */
export const SignUpStep4 = () => {
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);

    // 진입 시 국가 1회만 derive.
    //   1순위: SNS 원본 번호를 파싱해 얻은 ISO2(ctnCountry).
    //   2순위: ctnCountryCd → ISO2 역산. 단 calling code 는 국가를 특정하지 못한다
    //          (+1 = US/CA 포함 25개국, +7 = RU/KZ) — find() 가 알파벳순 첫 국가를 집으므로
    //          +1 은 AG(앤티가 바부다), +7 은 KZ 가 된다. 구버전 앱(ctnCountry 미제공) 대비 fallback 으로만 둔다.
    //   3순위: 거주 국가 → US.
    const [initialCountry] = useState<Country>(() => {
        if (signUpData.ctnCountry && getCountries().includes(signUpData.ctnCountry as Country)) {
            return signUpData.ctnCountry as Country;
        }
        if (signUpData.ctnCountryCd) {
            const callingCode = signUpData.ctnCountryCd.replace("+", "");
            const matched = getCountries().find(c => getCountryCallingCode(c) === callingCode);
            if (matched) return matched;
        }
        if (signUpData.countryCd) {
            const countries = getCountries();
            if (countries.includes(signUpData.countryCd as Country)) {
                return signUpData.countryCd as Country;
            }
        }
        return "US" as Country;
    });

    const handleNext = (result: IdentityGlobalPhoneResult) => {
        // 주의: countryCd 는 거주국가(분기 기준) 라서 갱신하지 않음.
        //   여기서 picker 로 고른 result.country 는 phone 의 국가코드 결정용일 뿐.
        //   countryCd 까지 덮어쓰면 시뮬에서 KR 선택 시 Step5 분기가 다날로 잘못 빠짐.
        setSignUpData(prev => ({
            ...prev,
            ctn: result.phone,
            ctnCountryCd: result.ctnCountryCd,
            // 사용자가 picker 로 바꿨을 수 있으므로 SNS 파싱값을 덮어쓴다.
            ctnCountry: result.country,
        }));
        pushStep(5);
    };

    return (
        <IdentityGlobalPhoneForm
            initialCountry={initialCountry}
            initialPhone={signUpData.ctn || ""}
            // 14세 미만 본인 인증 시, 보호자 번호와 동일하면 안 됨 (기존 다날 보호자 번호 / 법정대리인 동의 보호자 번호)
            parentCtn={signUpData.parentCtn || (signUpData.guardianRequired ? signUpData.guardianCtn : "") || undefined}
            // 기존 다날 보호자는 국내(+82), 법정대리인 동의 보호자는 입력한 국가코드
            parentCtnCountryCd={signUpData.parentCtn ? "+82" : (signUpData.guardianCtnCountryCd || "+82")}
            onNext={handleNext}
        />
    );
};
